// 프레임 '작업 시간' 측정 — vsync와 무관하게 실제 부하를 잰다.
// FPS는 모니터 주사율에 막혀 여력을 못 보여준다(1·2차 측정 실패의 원인).
// CPU: update + 렌더 제출 시간. GPU: EXT_disjoint_timer_query_webgl2가 있으면 실제 GPU 시간.

export class WorkTimer {
  constructor(game) {
    this.game = game;
    this.cpu = [];
    this.gpu = [];
    this.t0 = 0;

    const renderer = game.renderer;
    this.gl = renderer?.gl ?? null;
    this.ext = null;
    this.queries = [];
    this.pending = [];

    if (this.gl && typeof this.gl.createQuery === 'function') {
      this.ext = this.gl.getExtension('EXT_disjoint_timer_query_webgl2');
    }
    this.gpuSupported = !!this.ext;
    // 확장이 없으면 동기화 모드로 — CPU 시계가 GPU 완료까지 포함한다
    this.syncMode = !this.ext;
    this._px = new Uint8Array(4);
  }

  // 측정 방식 라벨 — 결과를 읽는 사람이 무엇을 보고 있는지 알아야 한다
  get mode() {
    return this.ext ? 'GPU 타이머' : (this.syncMode ? 'CPU+GPU 동기화 실측' : 'CPU만');
  }

  begin() {
    this.t0 = performance.now();
    if (!this.ext) return;
    const gl = this.gl;
    const q = this.queries.pop() ?? gl.createQuery();
    try {
      gl.beginQuery(this.ext.TIME_ELAPSED_EXT, q);
      this.active = q;
    } catch { this.active = null; }
  }

  end() {
    // 타이머 확장이 막혀 있으면(Chrome 기본) GPU를 강제 동기화해 CPU 시계가 GPU 완료까지 포함하게 한다.
    // gl.finish()만으로는 구현에 따라 no-op일 수 있어, 1픽셀 readPixels로 확실히 파이프라인을 비운다.
    // CPU/GPU 오버랩이 사라지므로 값은 약간 비관적 = 판정에는 안전한 방향.
    if (!this.ext && this.gl && this.syncMode) {
      const gl = this.gl;
      try {
        gl.finish();
        gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, this._px);
      } catch {}
    }
    this.cpu.push(performance.now() - this.t0);
    if (!this.ext || !this.active) return;
    const gl = this.gl;
    try { gl.endQuery(this.ext.TIME_ELAPSED_EXT); this.pending.push(this.active); } catch {}
    this.active = null;
    this.drain();
  }

  drain() {
    const gl = this.gl;
    while (this.pending.length) {
      const q = this.pending[0];
      const available = gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE);
      const disjoint = gl.getParameter(this.ext.GPU_DISJOINT_EXT);
      if (!available) break;
      this.pending.shift();
      if (!disjoint) this.gpu.push(gl.getQueryParameter(q, gl.QUERY_RESULT) / 1e6); // ns → ms
      this.queries.push(q);
    }
  }

  reset() { this.cpu = []; this.gpu = []; }

  stats() {
    const q = (arr, p) => {
      if (!arr.length) return null;
      const s = [...arr].sort((a, b) => a - b);
      return s[Math.min(s.length - 1, Math.floor(s.length * p))];
    };
    const avg = arr => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
    return {
      cpuAvg: avg(this.cpu), cpuP95: q(this.cpu, 0.95), cpuN: this.cpu.length,
      gpuAvg: avg(this.gpu), gpuP95: q(this.gpu, 0.95), gpuN: this.gpu.length,
      gpuSupported: this.gpuSupported,
      mode: this.mode,
      // 동기화 모드에서는 cpuAvg가 곧 'CPU+GPU 총 작업 시간'이다
      workAvg: this.gpuSupported ? Math.max(avg(this.cpu) ?? 0, avg(this.gpu) ?? 0) : avg(this.cpu),
      workP95: this.gpuSupported ? Math.max(q(this.cpu, 0.95) ?? 0, q(this.gpu, 0.95) ?? 0) : q(this.cpu, 0.95),
    };
  }
}

// 16.67ms = 60fps 예산. 작업 시간이 이걸 넘으면 60fps 불가.
export const BUDGET_60 = 1000 / 60;
